import { useEffect, useState } from "react";
import { useParams, useSearchParams, useLocation } from "react-router-dom";
import { ShieldOff, IdCard, MessageCircle, Sun, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { cameraLabels } from "../../lib/kycApi";
import { KycShell, BigButton, BrandMark, acento } from "../../components/kyc/KycShell";
import { KycFlow } from "../../components/kyc/KycFlow";
import { DIDIT_KEY } from "../../components/kyc/StepDidit";

const NEEDS = [
  { icon: IdCard, t: "Tu carnet" },
  { icon: MessageCircle, t: "Tu celular con WhatsApp" },
  { icon: Sun, t: "Buena luz" },
];

export const Unavailable = () => (
  <div className="min-h-screen grid place-items-center bg-slate-50 p-6" data-testid="kyc-unavailable">
    <div className="text-center space-y-3">
      <ShieldOff className="mx-auto text-slate-400" size={48} />
      <h1 className="font-heading text-2xl font-bold">Este enlace no está disponible</h1>
      <p className="text-base text-slate-500">Contacta a la empresa que te envió este enlace.</p>
    </div>
  </div>
);

export default function KycPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const [brand, setBrand] = useState(undefined);
  const retorno = useLocation().pathname.endsWith("/didit");
  const [session, setSession] = useState(() => (retorno ? JSON.parse(sessionStorage.getItem(DIDIT_KEY) || "null") : null));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/public/k/${slug}`).then((r) => setBrand(r.data)).catch(() => setBrand(null));
  }, [slug]);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/public/k/${slug}/sesiones`, { camaras: await cameraLabels(), solicitud: params.get("s") || null });
      setSession(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (brand === undefined) return null;
  if (brand === null) return <Unavailable />;
  if (session) return <KycFlow key={session.session_id} brand={brand} session={session} retorno={retorno} onRestart={() => setSession(null)} />;
  return (
    <KycShell brand={brand} pantalla="bienvenida">
      <div className="flex flex-col items-center text-center space-y-8 pt-4" data-testid="kyc-welcome">
        <BrandMark brand={brand} size="h-20 w-20" texto="sr-only" />
        <div className="space-y-3">
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-slate-900" data-testid="kyc-welcome-title">Verifica tu identidad en 2 minutos</h1>
          <p className="text-[15px] text-slate-500">{brand.nombre} necesita conocerte antes de tus envíos de dinero.</p>
        </div>
        <ul className="grid grid-cols-3 gap-3 w-full">
          {NEEDS.map((n) => (
            <li key={n.t} className="flex flex-col items-center gap-2 rounded-2xl border border-slate-100 bg-slate-50 px-2 py-4 text-sm text-slate-700">
              <span className="h-11 w-11 rounded-full grid place-items-center bg-white shadow-sm"><n.icon size={20} style={{ color: acento(brand) }} /></span>
              {n.t}
            </li>
          ))}
        </ul>
        <BigButton color={brand.color} onClick={start} disabled={busy} data-testid="kyc-start-button">{busy ? "Preparando…" : "Comenzar"}</BigButton>
        <p className="flex items-center gap-2 text-xs text-slate-500" data-testid="kyc-welcome-secure"><Lock size={14} /> Tus datos viajan cifrados y protegidos</p>
      </div>
    </KycShell>
  );
}
